import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';
import { lockBodyScroll, unlockBodyScroll } from '../../lib/scroll-lock';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  side?: 'right' | 'left';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  dismissOnBackdrop?: boolean;
  dismissOnEsc?: boolean;
  hideHeader?: boolean;
  children: ReactNode;
  footer?: ReactNode;
}

// BRANDBOOK §6.9 — sm 380, md 480 (default), lg 720, xl full minus sidebar
const sizeClass: Record<NonNullable<DrawerProps['size']>, string> = {
  sm: 'max-w-[380px]',
  md: 'max-w-[480px]',
  lg: 'max-w-[720px]',
  xl: 'max-w-[calc(100vw-240px)]',
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Drawer({
  open,
  onClose,
  title,
  description,
  side = 'right',
  size = 'md',
  dismissOnBackdrop = true,
  dismissOnEsc = true,
  hideHeader = false,
  footer,
  children,
}: DrawerProps) {
  const { t } = useTranslation('components');
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // onClose/dismissOnEsc ref'te tutulur ki effect yalnız `open`'a bağlı kalsın.
  // Inline `onClose={() => ...}` her render'da yeni kimlik alır; effect deps'inde
  // olsaydı her tuş vuruşunda cleanup + yeniden focus döngüsü oluşurdu (focus,
  // her karakterden sonra ilk focusable butona zıplardı).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dismissOnEscRef = useRef(dismissOnEsc);
  dismissOnEscRef.current = dismissOnEsc;

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    lockBodyScroll();
    const t = setTimeout(() => {
      const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      first?.focus();
    }, 0);
    function onKey(e: KeyboardEvent) {
      if (dismissOnEscRef.current && e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = Array.from(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? []);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey, true);
      unlockBodyScroll();
      previouslyFocused.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  // BRANDBOOK §6.9 — right drawer (default), sol kenarda radius 16 (right side)
  const sideClass =
    side === 'right'
      ? 'right-0 rounded-l-custel-xl animate-[drawer-slide-in-right_250ms_cubic-bezier(0.2,0.9,0.3,1)]'
      : 'left-0 rounded-r-custel-xl animate-[drawer-slide-in-left_250ms_cubic-bezier(0.2,0.9,0.3,1)]';

  const node = (
    <>
      <button
        type="button"
        aria-label={t('common:actions.close')}
        onClick={() => dismissOnBackdrop && onClose()}
        className="fixed inset-0 z-40 animate-overlay-enter"
        style={{ backgroundColor: 'rgba(28, 27, 23, 0.4)' }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'drawer-title' : undefined}
        className={cn(
          'fixed top-0 z-50 flex h-full w-full flex-col bg-custel-paper shadow-custel-drawer',
          sideClass,
          sizeClass[size],
        )}
      >
        {!hideHeader && (title || description) && (
          <header className="flex items-start justify-between gap-3 border-b border-custel-border px-6 py-5">
            <div className="flex-1">
              {title && (
                <h2 id="drawer-title" className="text-[18px] font-semibold text-custel-ink">
                  {title}
                </h2>
              )}
              {description && <p className="mt-1 text-sm text-custel-g700">{description}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-custel-md text-custel-g500 transition-colors hover:bg-custel-g100 hover:text-custel-ink focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-ink"
              aria-label={t('common:actions.close')}
            >
              <X size={18} aria-hidden="true" />
            </button>
          </header>
        )}
        <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>
        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-custel-border bg-custel-paper px-6 py-4">
            {footer}
          </footer>
        )}
      </div>
    </>
  );

  return createPortal(node, document.body);
}
